/**
 * Repeat math, expansion / Overdue, wording and presets for the resident
 * planner (spec 2026-10-02 §4–§5).
 *
 * Dates are 'YYYY-MM-DD' keys. Every calculation runs on Date.UTC values built
 * from the key's parts — never local time, never new Date('YYYY-MM-DD') — so
 * neither the device time zone nor a DST change can shift a day. Pure:
 * nothing here reads the clock.
 *
 * The date math MIRRORS resio-core apps/lifestyle
 * src/api/services/resident-todos/recurrence.ts. The two test files share one
 * case table; change both together.
 */
import type {
  RepeatRule,
  ResidentTodo,
  ResidentTodoOccurrenceRecord,
  ResidentTodoRecord,
} from '@/types/residents/todos';
import { compareTodos } from './todoTime';

const DAY_MS = 86_400_000;

const pad2 = (n: number): string => String(n).padStart(2, '0');

const parts = (key: string): [number, number, number] => {
  const [y, m, d] = key.split('-').map(Number);
  return [y, m, d];
};

const keyOf = (y: number, m: number, d: number): string => `${y}-${pad2(m)}-${pad2(d)}`;

const utcMs = (key: string): number => {
  const [y, m, d] = parts(key);
  return Date.UTC(y, m - 1, d);
};

export const addDaysToKey = (key: string, n: number): string => {
  const date = new Date(utcMs(key) + n * DAY_MS);
  return keyOf(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const daysBetween = (from: string, to: string): number =>
  Math.round((utcMs(to) - utcMs(from)) / DAY_MS);

/** 0 = Sunday … 6 = Saturday. */
export const weekdayOfKey = (key: string): number => new Date(utcMs(key)).getUTCDay();

export const dayOfKey = (key: string): number => parts(key)[2];

const daysInMonth = (y: number, m: number): number => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** The day a monthly/yearly repeat lands on in month `m` of year `y`. */
const clampedDay = (rule: RepeatRule, y: number, m: number): number =>
  Math.min(rule.monthDay ?? 1, daysInMonth(y, m));

/** The rule's pattern alone, for a key on/after the start (bounds live in isScheduled). */
function matches(rule: RepeatRule, start: string, key: string): boolean {
  const [sy, sm] = parts(start);
  const [ky, km, kd] = parts(key);
  if (rule.frequency === 'daily') {
    return daysBetween(start, key) % rule.interval === 0;
  }
  if (rule.frequency === 'weekly') {
    if (!rule.weekdays?.includes(weekdayOfKey(key))) return false;
    // Sunday-start weeks, like the planner's week strip.
    const weeks =
      daysBetween(
        addDaysToKey(start, -weekdayOfKey(start)),
        addDaysToKey(key, -weekdayOfKey(key)),
      ) / 7;
    return weeks % rule.interval === 0;
  }
  if (rule.frequency === 'monthly') {
    const months = (ky - sy) * 12 + (km - sm);
    return months % rule.interval === 0 && kd === clampedDay(rule, ky, km);
  }
  return (ky - sy) % rule.interval === 0 && km === sm && kd === clampedDay(rule, ky, km);
}

/** Whether `key` is a scheduled date of the repeat that starts on `start`. */
export function isScheduled(rule: RepeatRule, start: string, key: string): boolean {
  if (key < start) return false;
  if (rule.until !== null && key > rule.until) return false;
  return matches(rule, start, key);
}

/** The repeat's first scheduled date, or null when it has none at all. */
export function firstDate(rule: RepeatRule, start: string): string | null {
  let first: string | null = null;
  if (rule.frequency === 'daily') {
    first = start;
  } else if (rule.frequency === 'weekly') {
    // The first match always falls within one interval of weeks of the start.
    for (let i = 0; i < 7 * rule.interval && first === null; i += 1) {
      const key = addDaysToKey(start, i);
      if (matches(rule, start, key)) first = key;
    }
  } else {
    // The start's own month (or year) — or, when its day falls before the
    // start, the next month (year) the interval lands on.
    const [sy, sm] = parts(start);
    for (let j = 0; j <= 1 && first === null; j += 1) {
      const index =
        rule.frequency === 'monthly'
          ? sy * 12 + (sm - 1) + j * rule.interval
          : (sy + j * rule.interval) * 12 + (sm - 1);
      const y = Math.floor(index / 12);
      const m = (index % 12) + 1;
      const key = keyOf(y, m, clampedDay(rule, y, m));
      if (key >= start) first = key;
    }
  }
  if (first === null) return null;
  return rule.until !== null && first > rule.until ? null : first;
}

/** Every scheduled date in [fromKey, toKey], ascending. Bounded by the range, not the history. */
export function datesBetween(
  rule: RepeatRule,
  start: string,
  fromKey: string,
  toKey: string,
): string[] {
  const out: string[] = [];
  const lo = fromKey > start ? fromKey : start;
  const hi = rule.until !== null && rule.until < toKey ? rule.until : toKey;
  for (let key = lo; key <= hi; key = addDaysToKey(key, 1)) {
    if (matches(rule, start, key)) out.push(key);
  }
  return out;
}

/**
 * The latest scheduled date strictly before `beforeKey`, or null. Arithmetic
 * for daily/monthly/yearly and a scan of at most one interval of weeks for
 * weekly — never a walk over the repeat's history.
 */
export function previousDate(rule: RepeatRule, start: string, beforeKey: string): string | null {
  let last = addDaysToKey(beforeKey, -1);
  if (rule.until !== null && rule.until < last) last = rule.until;
  if (last < start) return null;
  if (rule.frequency === 'daily') {
    const steps = Math.floor(daysBetween(start, last) / rule.interval);
    return addDaysToKey(start, steps * rule.interval);
  }
  if (rule.frequency === 'weekly') {
    for (let i = 0; i < 7 * rule.interval; i += 1) {
      const key = addDaysToKey(last, -i);
      if (key < start) return null;
      if (matches(rule, start, key)) return key;
    }
    return null;
  }
  // Monthly/yearly: the on-month (on-year) holding `last`, or the one before.
  const [sy, sm] = parts(start);
  const [ly, lm] = parts(last);
  const startIndex = sy * 12 + (sm - 1);
  const step = rule.frequency === 'monthly' ? rule.interval : rule.interval * 12;
  let index =
    rule.frequency === 'monthly'
      ? startIndex + Math.floor((ly * 12 + (lm - 1) - startIndex) / step) * step
      : startIndex + Math.floor((ly - sy) / rule.interval) * step;
  for (let j = 0; j <= 1; j += 1, index -= step) {
    if (index < startIndex) return null;
    const y = Math.floor(index / 12);
    const m = (index % 12) + 1;
    const key = keyOf(y, m, clampedDay(rule, y, m));
    if (key <= last && key >= start) return key;
  }
  return null;
}

// ── Items ──────────────────────────────────────────────────────────────────

/** A repeating item's id: unique per date, stable across renders. */
export const occurrenceItemId = (seriesId: string, date: string): string => `${seriesId}@${date}`;

/** A one-off as an item — today's shape, plus its time. */
const oneOffItem = (record: ResidentTodoRecord): ResidentTodo => ({
  id: record.id,
  title: record.title,
  dueDate: record.dueDate,
  dueTime: record.dueTime ?? undefined,
  completed: record.completed,
  notes: record.notes,
});

/** One scheduled date of a repeat as an item, or null when it was cancelled. */
function occurrenceItem(
  record: ResidentTodoRecord,
  byDate: Map<string, ResidentTodoOccurrenceRecord>,
  date: string,
  first: string | null,
): ResidentTodo | null {
  const entry = byDate.get(date);
  if (entry?.cancelled) return null;
  // A record with no notes (null, or no key at all) uses the repeat's notes.
  const notes = entry?.notes ?? record.notes;
  // Likewise the time: no time of its own (null, or no key) → the repeat's; '' = none on this date.
  const time = entry?.time ?? record.dueTime;
  return {
    id: occurrenceItemId(record.id, date),
    title: entry?.title ?? record.title,
    dueDate: entry?.movedTo ?? date,
    dueTime: time ? time : undefined,
    completed: entry?.completed ?? false,
    // '' = cleared for this date.
    notes: notes ? notes : undefined,
    seriesId: record.id,
    occurrenceDate: date,
    seriesStart: record.dueDate,
    repeat: record.repeat as RepeatRule,
    isFirstDate: date === first,
  };
}

const isMoved = (entry: ResidentTodoOccurrenceRecord | undefined): boolean =>
  !!entry?.movedTo && entry.movedTo !== entry.date;

/**
 * Every item shown in [fromKey, toKey]: one-offs dated in it, plus each repeat's
 * dates in it — a moved date shows on its new day, so it can come in from
 * outside the range or leave it.
 */
export function expandTodos(
  records: ResidentTodoRecord[],
  fromKey: string,
  toKey: string,
): ResidentTodo[] {
  const items: ResidentTodo[] = [];
  for (const record of records) {
    if (!record.repeat) {
      if (record.dueDate >= fromKey && record.dueDate <= toKey) items.push(oneOffItem(record));
      continue;
    }
    const rule = record.repeat;
    const first = firstDate(rule, record.dueDate);
    const byDate = new Map(record.occurrences.map(o => [o.date, o]));
    for (const date of datesBetween(rule, record.dueDate, fromKey, toKey)) {
      if (isMoved(byDate.get(date))) continue; // shown on its new day instead
      const item = occurrenceItem(record, byDate, date, first);
      if (item) items.push(item);
    }
    for (const entry of record.occurrences) {
      if (!isMoved(entry) || entry.cancelled) continue;
      const shownOn = entry.movedTo as string;
      if (shownOn < fromKey || shownOn > toKey) continue;
      if (!isScheduled(rule, record.dueDate, entry.date)) continue;
      const item = occurrenceItem(record, byDate, entry.date, first);
      if (item) items.push(item);
    }
  }
  return items;
}

/**
 * A repeat's latest visible item shown before today, or null. When several share
 * that latest shown day (a date moved onto another), an unticked one wins, then
 * the later scheduled date — whatever order the records come in (spec §5.2).
 */
function latestPastItem(record: ResidentTodoRecord, todayKey: string): ResidentTodo | null {
  const rule = record.repeat as RepeatRule;
  const first = firstDate(rule, record.dueDate);
  const byDate = new Map(record.occurrences.map(o => [o.date, o]));
  // The latest scheduled date still shown on its own day (cancelled and moved skipped).
  let plain = previousDate(rule, record.dueDate, todayKey);
  while (plain !== null) {
    const entry = byDate.get(plain);
    if (!entry || (!entry.cancelled && !isMoved(entry))) break;
    plain = previousDate(rule, record.dueDate, plain);
  }
  // A date moved onto a past day competes with it.
  type Candidate = { date: string; shownOn: string };
  const candidates: Candidate[] = plain === null ? [] : [{ date: plain, shownOn: plain }];
  for (const entry of record.occurrences) {
    if (entry.cancelled || !isMoved(entry)) continue;
    const shownOn = entry.movedTo as string;
    if (shownOn >= todayKey || !isScheduled(rule, record.dueDate, entry.date)) continue;
    candidates.push({ date: entry.date, shownOn });
  }
  // Latest shown day wins; on a tie an unticked date beats a ticked one, then
  // the later scheduled date.
  const rank = (c: Candidate) => `${c.shownOn}|${byDate.get(c.date)?.completed ? 0 : 1}|${c.date}`;
  const best = candidates.reduce<Candidate | null>(
    (top, c) => (top === null || rank(c) > rank(top) ? c : top),
    null,
  );
  return best === null ? null : occurrenceItem(record, byDate, best.date, first);
}

/**
 * The Overdue rows (spec §5.2): unticked past one-offs, plus at most ONE row
 * per repeat — its latest past date, if unticked. Older missed dates never
 * take its place. Oldest first; within a day, the day's order — timed first by
 * time, then by title.
 *
 * "Latest" is by SHOWN day (a date moved onto a past day competes there). When
 * several of a repeat's items share that latest shown day, the tie goes to the
 * unticked one over a ticked one, then to the later scheduled date.
 */
export function overdueTodos(records: ResidentTodoRecord[], todayKey: string): ResidentTodo[] {
  const rows: ResidentTodo[] = [];
  for (const record of records) {
    if (!record.repeat) {
      if (!record.completed && record.dueDate < todayKey) rows.push(oneOffItem(record));
      continue;
    }
    const latest = latestPastItem(record, todayKey);
    if (latest && !latest.completed) rows.push(latest);
  }
  return rows.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || compareTodos(a, b));
}

// ── Wording + presets ──────────────────────────────────────────────────────

const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Weekday sets with a name of their own, keyed as `sortedWeekdays` writes them: `everyWeek` is the
 * whole phrase at an interval of 1, `on` follows "Every N weeks on". All seven days lands on the
 * same dates as Daily, so it reads the same.
 */
const NAMED_DAYS: Record<string, { everyWeek: string; on: string }> = {
  '0,1,2,3,4,5,6': { everyWeek: 'Every day', on: 'all days' },
  '1,2,3,4,5': { everyWeek: 'Every weekday', on: 'weekdays' },
  '0,6': { everyWeek: 'Every weekend', on: 'weekends' },
};

export const ordinal = (n: number): string => {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
};

const every = (interval: number, unit: string): string =>
  interval === 1 ? `Every ${unit}` : `Every ${interval} ${unit}s`;

/**
 * "Every 2 weeks on Mon, Wed, Fri", "Every month on the 31st", "Every year on Oct 6".
 *
 * `startKey` is the repeat's start or an UNCLAMPED scheduled date (a yearly rule
 * takes its month from it) — never a moved (shown) date. The end date is left
 * out of the phrase: Ends is its own row.
 */
export function describeRepeat(rule: RepeatRule, startKey: string): string {
  if (rule.frequency === 'daily') return every(rule.interval, 'day');
  if (rule.frequency === 'weekly') {
    const named = NAMED_DAYS[sortedWeekdays(rule)];
    if (named) {
      return rule.interval === 1 ? named.everyWeek : `${every(rule.interval, 'week')} on ${named.on}`;
    }
    const days = rule.weekdays ?? [];
    const on = days.length === 1 ? WEEKDAY_LONG[days[0]] : days.map(d => WEEKDAY_SHORT[d]).join(', ');
    return `${every(rule.interval, 'week')} on ${on}`;
  }
  if (rule.frequency === 'monthly') {
    return `${every(rule.interval, 'month')} on the ${ordinal(rule.monthDay ?? 1)}`;
  }
  return `${every(rule.interval, 'year')} on ${MONTH_SHORT[parts(startKey)[1] - 1]} ${rule.monthDay ?? 1}`;
}

export type RepeatPreset = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'yearly';

export const REPEAT_PRESETS: Array<{ preset: RepeatPreset; label: string }> = [
  { preset: 'daily', label: 'Daily' },
  { preset: 'weekly', label: 'Weekly' },
  { preset: 'biweekly', label: 'Every 2 weeks' },
  { preset: 'monthly', label: 'Monthly' },
  { preset: 'yearly', label: 'Yearly' },
];

/** A preset as a rule, hung off the start date (its weekday / day of month). */
export function presetRule(
  preset: RepeatPreset,
  startKey: string,
  until: string | null = null,
): RepeatRule {
  const weekday = weekdayOfKey(startKey);
  const day = dayOfKey(startKey);
  if (preset === 'daily') return { frequency: 'daily', interval: 1, weekdays: null, monthDay: null, until };
  if (preset === 'weekly' || preset === 'biweekly') {
    return { frequency: 'weekly', interval: preset === 'weekly' ? 1 : 2, weekdays: [weekday], monthDay: null, until };
  }
  return { frequency: preset, interval: 1, weekdays: null, monthDay: day, until };
}

/** The weekdays as an ascending key, sorted on a copy so the rule's own list keeps its order. */
const sortedWeekdays = (rule: RepeatRule): string =>
  [...(rule.weekdays ?? [])].sort((x, y) => x - y).join(',');

/** Same rule apart from the end date. Weekday order does not matter; a missing monthDay is null. */
const sameShape = (a: RepeatRule, b: RepeatRule): boolean =>
  a.frequency === b.frequency &&
  a.interval === b.interval &&
  (a.monthDay ?? null) === (b.monthDay ?? null) &&
  sortedWeekdays(a) === sortedWeekdays(b);

/**
 * The preset chip a rule shows as; 'custom' when it matches none. The end date
 * does not matter.
 *
 * `startKey` is the repeat's start or an UNCLAMPED scheduled date (the presets
 * take their weekday / day of month from it) — never a moved (shown) date. A
 * clamped date (Feb 28 of a 31st rule) is not one: its day is not the rule's
 * monthDay, so a monthly or yearly rule matched against it comes back 'custom'.
 */
export function matchPreset(rule: RepeatRule, startKey: string): RepeatPreset | 'custom' {
  return REPEAT_PRESETS.find(p => sameShape(rule, presetRule(p.preset, startKey)))?.preset ?? 'custom';
}

/** Rule parts derived from the old date move to the new one; hand-picked parts stay (spec §5.3). */
export function followDate(rule: RepeatRule, oldKey: string, newKey: string): RepeatRule {
  const next: RepeatRule = { ...rule };
  if (
    rule.frequency === 'weekly' &&
    rule.weekdays?.length === 1 &&
    rule.weekdays[0] === weekdayOfKey(oldKey)
  ) {
    next.weekdays = [weekdayOfKey(newKey)];
  }
  if (
    (rule.frequency === 'monthly' || rule.frequency === 'yearly') &&
    rule.monthDay === dayOfKey(oldKey)
  ) {
    next.monthDay = dayOfKey(newKey);
  }
  return next;
}

export const sameRule = (a: RepeatRule | null, b: RepeatRule | null): boolean =>
  a === b ||
  (a !== null && b !== null && sameShape(a, b) && (a.until ?? null) === (b.until ?? null));
